class FeelOriCrowdDrop extends HTMLElement {
  connectedCallback() {
    this.endpoint = this.dataset.endpoint || '';
    this.campaignId = this.dataset.campaignId || '';
    this.initialCampaignId = this.campaignId;
    this.currency = this.dataset.currency || 'INR';
    this.shareMessage = this.dataset.shareMessage || 'Join the FeelOri CrowdDrop:';
    this.form = this.querySelector('[data-reservation-form]');
    this.submitButton = this.querySelector('[data-submit]');
    this.status = this.querySelector('[data-form-status]');
    this.deadline = this.dataset.deadline || '';
    this.configuredReady = this.dataset.reservationReady === 'true';
    this.formDisabled = !this.configuredReady;
    this.captchaSiteKey = this.dataset.captchaSiteKey || '';
    this.deviceToken = this.readDeviceToken();

    this.setupMediaGallery();
    this.setupCaptcha();
    this.setupCampaignShare();
    this.setupCampaignBoard();
    this.setupCandidateVoting();
    this.startCountdown();
    this.form?.addEventListener('submit', (event) => this.submitReservation(event));
    this.querySelector('[data-verification-form]')?.addEventListener('submit', (event) => this.verifyReservation(event));
    this.querySelector('[data-verification-resend]')?.addEventListener('click', () => this.resendVerification());
    this.querySelector('[data-copy-referral]')?.addEventListener('click', () => this.copyReferral());
    this.querySelector('[data-native-share]')?.addEventListener('click', () => this.shareReferral());
    this.querySelector('[data-instagram-share]')?.addEventListener('click', () => this.shareInstagramStory());

    if (this.endpoint && this.campaignId) {
      this.refreshStatus();
      this.refreshWhenVisible = () => {
        if (document.visibilityState === 'visible') {
          this.refreshStatus();
          this.loadCampaignBoard();
        }
      };
      window.addEventListener('focus', this.refreshWhenVisible);
      document.addEventListener('visibilitychange', this.refreshWhenVisible);
    }
  }

  disconnectedCallback() {
    window.clearInterval(this.countdownTimer);
    window.removeEventListener('focus', this.refreshWhenVisible);
    document.removeEventListener('visibilitychange', this.refreshWhenVisible);
  }

  setupCampaignShare() {
    this.showSharePanel();
  }

  setupCampaignBoard() {
    this.board = this.querySelector('[data-campaign-board]');
    this.boardStatus = this.querySelector('[data-campaign-board-status]');
    if (!this.board || !this.endpoint) return;
    this.loadCampaignBoard();
  }

  setupCandidateVoting() {
    this.ballotKey = this.dataset.ballotKey || '';
    this.candidateStatus = this.querySelector('[data-candidate-status]');
    this.candidateButtons = [...this.querySelectorAll('[data-candidate-vote]')];
    if (!this.candidateButtons.length) return;

    let savedHandle = '';
    try { savedHandle = window.localStorage.getItem(`feelori-crowddrop-vote:${this.ballotKey}`) || ''; } catch (_error) { savedHandle = ''; }
    this.candidateButtons.forEach((button) => {
      button.addEventListener('click', () => this.submitCandidateVote(button));
      if (button.dataset.candidateVote === savedHandle) this.markCandidateVote(savedHandle);
    });
  }

  async submitCandidateVote(button) {
    if (!this.endpoint || !this.ballotKey) {
      if (this.candidateStatus) this.candidateStatus.textContent = 'Voting is not available yet.';
      return;
    }
    const productHandle = button.dataset.candidateVote || '';
    const originalLabel = button.textContent;
    this.candidateButtons.forEach((candidate) => { candidate.disabled = true; });
    button.textContent = 'Saving your choice…';
    if (this.candidateStatus) this.candidateStatus.textContent = '';
    try {
      const response = await fetch(this.endpoint, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
        body: new URLSearchParams({ payload: JSON.stringify({
          action: 'vote',
          ballot_key: this.ballotKey,
          product_handle: productHandle,
          device_token: this.deviceToken,
        }) }),
      });
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.error || 'Your vote could not be saved.');
      try { window.localStorage.setItem(`feelori-crowddrop-vote:${this.ballotKey}`, productHandle); } catch (_error) { /* The server remains authoritative when browser storage is unavailable. */ }
      this.markCandidateVote(productHandle);
      if (this.candidateStatus) this.candidateStatus.textContent = 'Your choice is saved. You can change it any time while voting is open.';
    } catch (error) {
      button.textContent = originalLabel;
      if (this.candidateStatus) this.candidateStatus.textContent = error instanceof Error ? error.message : 'Your vote could not be saved. Please try again.';
    } finally {
      this.candidateButtons.forEach((candidate) => { candidate.disabled = false; });
    }
  }

  markCandidateVote(productHandle) {
    this.candidateButtons.forEach((button) => {
      const selected = button.dataset.candidateVote === productHandle;
      button.setAttribute('aria-pressed', String(selected));
      button.textContent = selected ? 'Your choice ✓' : 'Vote for this piece';
      const card = button.closest('[data-candidate-card]');
      if (card) card.dataset.selected = String(selected);
    });
  }

  async loadCampaignBoard() {
    if (!this.board || !this.endpoint) return;
    try {
      const url = new URL(this.endpoint, window.location.origin);
      url.searchParams.delete('campaign_id');
      url.searchParams.set('view', 'board');
      const response = await fetch(url, { headers: { Accept: 'application/json' }, credentials: 'same-origin' });
      if (!response.ok) throw new Error('Campaign board unavailable');
      const payload = await response.json();
      const campaigns = Array.isArray(payload.campaigns) ? payload.campaigns.slice(0, 10) : [];
      if (!campaigns.length) throw new Error('No public campaigns');
      this.campaigns = campaigns;
      this.renderCampaignBoard(campaigns);

      const requestedSlug = new URLSearchParams(window.location.search).get('drop');
      const requestedCampaign = requestedSlug && campaigns.find((campaign) => (campaign.slug || campaign.id) === requestedSlug);
      if (requestedCampaign && requestedSlug !== this.campaignId) this.selectCampaign(requestedCampaign, false);
      if (this.boardStatus) {
        this.boardStatus.textContent = campaigns.length === 1
          ? 'One drop is live now. More jewellery drops can be added here without creating another Shopify page.'
          : `${campaigns.length} current CrowdDrops. Choose a product to see its exact terms.`;
      }
    } catch (_error) {
      if (this.boardStatus) this.boardStatus.textContent = 'Showing the featured CrowdDrop. More current drops will appear here when published.';
    }
  }

  renderCampaignBoard(campaigns) {
    if (!this.board) return;
    const fragment = document.createDocumentFragment();
    campaigns.forEach((campaign) => {
      const slug = campaign.slug || campaign.id;
      const startPrice = this.asNumber(campaign.start_price);
      const floorPrice = this.asNumber(campaign.floor_price);
      const currentPrice = this.asNumber(campaign.current_price);
      const reduction = this.asNumber(campaign.reduction_per_reservation);
      const verified = this.asNumber(campaign.verified_count) || 0;
      const remaining = floorPrice !== null && currentPrice !== null && reduction && reduction > 0
        ? Math.max(0, Math.ceil(Math.max(0, currentPrice - floorPrice) / reduction))
        : 0;
      const media = Array.isArray(campaign.product_media) ? campaign.product_media[0] : null;

      const card = document.createElement('article');
      card.className = 'fo-crowddrop__drop-card';
      card.dataset.campaignSlug = slug;
      card.setAttribute('aria-current', String(slug === this.campaignId));

      const mediaWrap = document.createElement('div');
      mediaWrap.className = 'fo-crowddrop__drop-media';
      if (media?.previewUrl || media?.url) {
        const image = document.createElement('img');
        image.src = media.previewUrl || media.url;
        image.alt = media.alt || campaign.product_title || 'FeelOri CrowdDrop jewellery';
        image.loading = 'lazy';
        mediaWrap.appendChild(image);
      } else {
        const placeholder = document.createElement('span');
        placeholder.textContent = 'Next jewellery drop';
        mediaWrap.appendChild(placeholder);
      }
      const state = document.createElement('span');
      state.className = 'fo-crowddrop__drop-state';
      state.textContent = this.campaignStatusLabel(campaign.status);
      mediaWrap.appendChild(state);
      card.appendChild(mediaWrap);

      const copy = document.createElement('div');
      copy.className = 'fo-crowddrop__drop-copy';
      const title = document.createElement('h3');
      title.textContent = campaign.product_title || 'FeelOri CrowdDrop';
      copy.appendChild(title);
      const prices = document.createElement('div');
      prices.className = 'fo-crowddrop__drop-prices';
      const current = document.createElement('strong');
      current.textContent = currentPrice === null ? 'Price coming soon' : this.money(currentPrice);
      const start = document.createElement('span');
      start.textContent = startPrice === null ? '' : `starts at ${this.money(startPrice)}`;
      prices.append(current, start);
      copy.appendChild(prices);
      const progress = document.createElement('p');
      progress.textContent = remaining === 0 ? `${verified} verified · lowest price unlocked` : `${verified} verified · ${remaining} to lowest price`;
      copy.appendChild(progress);
      const button = document.createElement('button');
      button.type = 'button';
      button.textContent = slug === this.campaignId ? 'Viewing this drop' : 'View this drop';
      button.addEventListener('click', () => this.selectCampaign(campaign, true));
      copy.appendChild(button);
      card.appendChild(copy);
      fragment.appendChild(card);
    });
    this.board.replaceChildren(fragment);
  }

  campaignStatusLabel(status) {
    return { open: 'Live now', scheduled: 'Opening soon', paused: 'Temporarily paused', closed: 'Final price locked' }[status] || 'CrowdDrop';
  }

  selectCampaign(campaign, scrollToDetails) {
    const slug = campaign.slug || campaign.id;
    if (!slug) return;
    const changed = slug !== this.campaignId;
    this.campaignId = slug;
    this.currency = campaign.currency || this.currency;
    this.shareMessage = campaign.share_message || this.shareMessage;
    this.dataset.storyTitle = campaign.heading || campaign.product_title || this.dataset.storyTitle;
    this.formDisabled = !this.configuredReady || campaign.status !== 'open';
    if (this.submitButton) this.submitButton.disabled = this.formDisabled;
    this.setText('[data-product-title]', campaign.product_title || 'FeelOri CrowdDrop');
    this.setText('[data-campaign-heading]', campaign.heading || 'One shared price, unlocked together.');
    const productLink = this.querySelector('[data-product-link]');
    if (productLink && campaign.product_url) productLink.href = campaign.product_url;
    if (changed) this.renderCampaignMedia(campaign.product_media, campaign.product_title);
    this.applyCampaign(campaign);
    this.showSharePanel();
    if (this.campaigns) this.renderCampaignBoard(this.campaigns);

    const pageUrl = new URL(window.location.href);
    if (slug === this.initialCampaignId) pageUrl.searchParams.delete('drop');
    else pageUrl.searchParams.set('drop', slug);
    window.history.replaceState({}, '', pageUrl);
    if (scrollToDetails) this.querySelector('[data-product-title]')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  renderCampaignMedia(mediaItems, productTitle) {
    const stage = this.querySelector('[data-media-stage]');
    if (!stage || !Array.isArray(mediaItems) || !mediaItems.length) return;
    stage.replaceChildren();
    mediaItems.slice(0, 12).forEach((media, index) => {
      const slide = document.createElement('div');
      slide.className = 'fo-crowddrop__media-slide';
      slide.dataset.mediaSlide = '';
      slide.dataset.mediaType = media.type || 'image';
      slide.hidden = index !== 0;
      if (media.type === 'video') {
        const video = document.createElement('video');
        video.src = media.url;
        video.poster = media.previewUrl || '';
        video.controls = true;
        video.playsInline = true;
        video.preload = 'metadata';
        video.setAttribute('aria-label', media.alt || `${productTitle} video`);
        slide.appendChild(video);
      } else if (media.type === 'external_video') {
        const frame = document.createElement('iframe');
        frame.src = media.url;
        frame.loading = 'lazy';
        frame.allow = 'accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture';
        frame.allowFullscreen = true;
        frame.title = media.alt || `${productTitle} video`;
        slide.appendChild(frame);
      } else if (media.type === 'model') {
        const model = document.createElement('model-viewer');
        model.src = media.url;
        model.poster = media.previewUrl || '';
        model.setAttribute('camera-controls', '');
        model.setAttribute('alt', media.alt || `${productTitle} 3D view`);
        slide.appendChild(model);
      } else {
        const image = document.createElement('img');
        image.src = media.url || media.previewUrl;
        image.alt = media.alt || productTitle || 'FeelOri CrowdDrop jewellery';
        image.loading = index === 0 ? 'eager' : 'lazy';
        slide.appendChild(image);
      }
      stage.appendChild(slide);
    });
    let nav = this.querySelector('.fo-crowddrop__media-nav');
    if (!nav) {
      nav = document.createElement('div');
      nav.className = 'fo-crowddrop__media-nav';
      stage.insertAdjacentElement('afterend', nav);
    }
    nav.replaceChildren();
    nav.hidden = mediaItems.length <= 1;
    if (mediaItems.length > 1) {
      nav.setAttribute('aria-label', 'Product media controls');
      const previous = document.createElement('button');
      previous.type = 'button';
      previous.dataset.mediaPrevious = '';
      previous.setAttribute('aria-label', 'Previous product media');
      previous.textContent = '‹';
      const thumbnails = document.createElement('div');
      thumbnails.className = 'fo-crowddrop__media-thumbnails';
      mediaItems.slice(0, 12).forEach((media, index) => {
        const control = document.createElement('button');
        control.type = 'button';
        control.dataset.mediaTarget = String(index);
        control.setAttribute('aria-label', `View media ${index + 1} of ${Math.min(12, mediaItems.length)}`);
        control.setAttribute('aria-current', String(index === 0));
        const thumb = document.createElement('img');
        thumb.src = media.previewUrl || media.url;
        thumb.alt = '';
        thumb.loading = 'lazy';
        control.appendChild(thumb);
        if (media.type === 'video' || media.type === 'external_video') {
          const play = document.createElement('span');
          play.textContent = '▶';
          play.setAttribute('aria-hidden', 'true');
          control.appendChild(play);
        }
        thumbnails.appendChild(control);
      });
      const next = document.createElement('button');
      next.type = 'button';
      next.dataset.mediaNext = '';
      next.setAttribute('aria-label', 'Next product media');
      next.textContent = '›';
      nav.append(previous, thumbnails, next);
    }
    let mediaStatus = this.querySelector('[data-media-status]');
    if (!mediaStatus) {
      mediaStatus = document.createElement('p');
      mediaStatus.className = 'visually-hidden';
      mediaStatus.dataset.mediaStatus = '';
      mediaStatus.setAttribute('role', 'status');
      mediaStatus.setAttribute('aria-live', 'polite');
      nav.insertAdjacentElement('afterend', mediaStatus);
    }
    mediaStatus.textContent = `Media 1 of ${Math.min(12, mediaItems.length)}`;
    this.setupMediaGallery();
  }

  readDeviceToken() {
    const key = 'feelori_crowddrop_device';
    try {
      const existing = window.localStorage.getItem(key);
      if (existing) return existing;
      const token = window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`;
      window.localStorage.setItem(key, token);
      return token;
    } catch (_error) {
      return `${Date.now()}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`;
    }
  }

  setupCaptcha() {
    const target = this.querySelector('[data-captcha]');
    if (!target || !this.captchaSiteKey) return;
    const render = () => {
      if (!window.turnstile || target.dataset.rendered === 'true') return;
      target.dataset.rendered = 'true';
      window.turnstile.render(target, {
        sitekey: this.captchaSiteKey,
        appearance: 'interaction-only',
        callback: (token) => { this.captchaToken = token; },
        'expired-callback': () => { this.captchaToken = ''; },
        'error-callback': () => { this.captchaToken = ''; },
      });
    };
    if (window.turnstile) { render(); return; }
    let script = document.querySelector('script[data-feelori-turnstile]');
    if (!script) {
      script = document.createElement('script');
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
      script.async = true;
      script.defer = true;
      script.dataset.feeloriTurnstile = 'true';
      document.head.appendChild(script);
    }
    script.addEventListener('load', render, { once: true });
  }

  setupMediaGallery() {
    this.mediaGalleryAbort?.abort();
    this.mediaGalleryAbort = new AbortController();
    const signal = this.mediaGalleryAbort.signal;
    this.mediaSlides = Array.from(this.querySelectorAll('[data-media-slide]'));
    if (this.mediaSlides.length === 0) return;

    this.mediaControls = Array.from(this.querySelectorAll('[data-media-target]'));
    this.mediaStatus = this.querySelector('[data-media-status]');
    this.mediaStage = this.querySelector('[data-media-stage]');
    this.activeMediaIndex = Math.max(
      0,
      this.mediaSlides.findIndex((slide) => !slide.hidden)
    );

    this.mediaControls.forEach((control) => {
      control.addEventListener('click', () => this.showMedia(Number(control.dataset.mediaTarget)), { signal });
    });
    this.querySelector('[data-media-previous]')?.addEventListener('click', () => this.showMedia(this.activeMediaIndex - 1), { signal });
    this.querySelector('[data-media-next]')?.addEventListener('click', () => this.showMedia(this.activeMediaIndex + 1), { signal });
    this.mediaStage?.addEventListener('keydown', (event) => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      event.preventDefault();
      this.showMedia(this.activeMediaIndex + (event.key === 'ArrowRight' ? 1 : -1));
    }, { signal });

    let touchStartX = null;
    this.mediaStage?.addEventListener(
      'touchstart',
      (event) => {
        touchStartX = event.changedTouches[0]?.clientX ?? null;
      },
      { passive: true, signal }
    );
    this.mediaStage?.addEventListener(
      'touchend',
      (event) => {
        if (touchStartX === null) return;
        const distance = (event.changedTouches[0]?.clientX ?? touchStartX) - touchStartX;
        touchStartX = null;
        if (Math.abs(distance) < 40) return;
        this.showMedia(this.activeMediaIndex + (distance < 0 ? 1 : -1));
      },
      { passive: true, signal }
    );

    this.showMedia(this.activeMediaIndex, false);
  }

  showMedia(index, announce = true) {
    if (!this.mediaSlides?.length || !Number.isFinite(index)) return;
    const nextIndex = ((index % this.mediaSlides.length) + this.mediaSlides.length) % this.mediaSlides.length;

    this.mediaSlides.forEach((slide, slideIndex) => {
      const active = slideIndex === nextIndex;
      slide.hidden = !active;
      slide.classList.toggle('is-active', active);
      if (!active) this.pauseMedia(slide);
    });
    this.mediaControls?.forEach((control) => {
      const active = Number(control.dataset.mediaTarget) === nextIndex;
      control.setAttribute('aria-current', String(active));
      if (active) control.scrollIntoView({ behavior: 'auto', block: 'nearest', inline: 'nearest' });
    });

    this.activeMediaIndex = nextIndex;
    if (this.mediaStatus) {
      this.mediaStatus.textContent = `Media ${nextIndex + 1} of ${this.mediaSlides.length}`;
      if (!announce) this.mediaStatus.setAttribute('aria-live', 'off');
      else this.mediaStatus.setAttribute('aria-live', 'polite');
    }
  }

  pauseMedia(slide) {
    slide.querySelectorAll('video').forEach((video) => video.pause());
    slide.querySelectorAll('iframe').forEach((frame) => {
      frame.contentWindow?.postMessage(JSON.stringify({ event: 'command', func: 'pauseVideo' }), '*');
      frame.contentWindow?.postMessage({ method: 'pause' }, '*');
    });
  }

  startCountdown() {
    const target = Date.parse(this.deadline);
    if (!Number.isFinite(target)) {
      if (this.deadline) this.disableForm('Reservations are not open yet. Please check back soon.');
      return;
    }
    this.setText('[data-deadline-label]', this.formatDeadline(target));

    const update = () => {
      const remaining = Math.max(0, target - Date.now());
      const days = Math.floor(remaining / 86400000);
      const hours = Math.floor((remaining % 86400000) / 3600000);
      const minutes = Math.floor((remaining % 3600000) / 60000);
      const seconds = Math.floor((remaining % 60000) / 1000);
      this.setText('[data-days]', days);
      this.setText('[data-hours]', String(hours).padStart(2, '0'));
      this.setText('[data-minutes]', String(minutes).padStart(2, '0'));
      this.setText('[data-seconds]', String(seconds).padStart(2, '0'));
      const compact =
        days > 0
          ? `${days}d ${hours}h remaining`
          : hours > 0
            ? `${hours}h ${minutes}m remaining`
            : `${minutes}m ${seconds}s remaining`;
      this.setText('[data-countdown-compact]', compact);

      if (remaining === 0) {
        window.clearInterval(this.countdownTimer);
        this.disableForm('This CrowdDrop has ended. Final checkout access is sent only to qualified participants.');
      }
    };

    update();
    this.countdownTimer = window.setInterval(update, 1000);
  }

  async refreshStatus() {
    try {
      const url = this.endpointUrl();
      const response = await fetch(url, { headers: { Accept: 'application/json' }, credentials: 'same-origin' });
      if (!response.ok) return;
      const payload = await response.json();
      this.applyCampaign(payload.campaign || payload);
    } catch (_error) {
      // The published Liquid values remain visible when the live endpoint is temporarily unavailable.
    }
  }

  endpointUrl() {
    const url = new URL(this.endpoint, window.location.origin);
    url.searchParams.set('campaign_id', this.campaignId);
    return url;
  }

  applyCampaign(campaign) {
    if (!campaign || typeof campaign !== 'object') return;

    const verified = this.asNumber(campaign.verified_count);
    const startPrice = this.asNumber(campaign.start_price);
    const floorPrice = this.asNumber(campaign.floor_price);
    const reduction = this.asNumber(campaign.reduction_per_reservation);
    const currentPrice = this.asNumber(campaign.current_price);
    const stock = this.asNumber(campaign.stock);

    if (verified !== null) this.setText('[data-verified-count]', verified);
    if (startPrice !== null) this.setText('[data-start-price]', this.money(startPrice));
    if (floorPrice !== null) this.setText('[data-floor-price]', this.money(floorPrice));
    if (reduction !== null) this.setText('[data-reduction]', this.money(reduction));
    if (currentPrice !== null) this.setText('[data-current-price]', this.money(currentPrice));
    if (stock !== null) this.setText('[data-stock]', stock);

    if (startPrice !== null && floorPrice !== null && currentPrice !== null) {
      const range = Math.max(0, startPrice - floorPrice);
      const progress = range === 0 ? 100 : Math.min(100, Math.max(0, ((startPrice - currentPrice) / range) * 100));
      const bar = this.querySelector('[data-progress-fill]');
      if (bar) {
        bar.style.width = `${progress}%`;
        bar.parentElement?.setAttribute('aria-valuenow', String(Math.round(progress)));
      }
    }

    if (floorPrice !== null && reduction !== null && currentPrice !== null && reduction > 0) {
      const remaining = Math.max(0, Math.ceil(Math.max(0, currentPrice - floorPrice) / reduction));
      const message = remaining === 0
        ? 'Lowest campaign price unlocked.'
        : `${remaining} more verified reservations to unlock the floor price.`;
      const floorStatus = this.querySelector('[data-floor-status]');
      if (floorStatus && floorStatus.textContent.trim() !== message) floorStatus.textContent = message;
    }

    if (campaign.deadline && campaign.deadline !== this.deadline) {
      this.deadline = campaign.deadline;
      window.clearInterval(this.countdownTimer);
      this.startCountdown();
    }

    if (campaign.status && campaign.status !== 'open') {
      const messages = {
        scheduled: 'Reservations are not open yet. Please check back soon.',
        closed: 'This CrowdDrop has ended. Final checkout access is sent only to qualified participants.',
        sold_out: 'This CrowdDrop is fully claimed.',
      };
      this.disableForm(messages[campaign.status] || 'Reservations are currently unavailable.');
    } else if (campaign.status === 'open' && this.configuredReady) {
      this.formDisabled = false;
      if (this.submitButton) this.submitButton.disabled = false;
    }

    if (verified !== null && reduction !== null && floorPrice !== null && currentPrice !== null) {
      const nextUnlock = currentPrice <= floorPrice
        ? 'The lowest published price is already unlocked.'
        : verified === 0
          ? `Be the first: once your email is verified, ${this.money(reduction)} unlocks for everyone—including you.`
          : `The next verified reservation unlocks another ${this.money(reduction)} for everyone.`;
      this.setText('[data-next-unlock-copy]', nextUnlock);
      this.renderPriceLadder(startPrice, floorPrice, reduction);
    }

    this.renderActivity(campaign.recent_activity);
  }

  renderPriceLadder(startPrice, floorPrice, reduction) {
    const ladder = this.querySelector('[data-price-ladder]');
    if (!ladder || startPrice === null || floorPrice === null || !reduction || reduction <= 0) return;
    const steps = Math.min(4, Math.max(0, Math.ceil(Math.max(0, startPrice - floorPrice) / reduction)));
    const fragment = document.createDocumentFragment();
    for (let index = 1; index <= steps; index += 1) {
      const item = document.createElement('div');
      const label = document.createElement('span');
      label.textContent = `${index} verified`;
      const price = document.createElement('strong');
      price.textContent = this.money(Math.max(floorPrice, startPrice - reduction * index));
      item.append(label, price);
      fragment.appendChild(item);
    }
    ladder.replaceChildren(fragment);
  }

  async submitReservation(event) {
    event.preventDefault();
    if (!this.form || !this.endpoint) return;

    const data = new FormData(this.form);
    const email = String(data.get('email') || '').trim();
    const phone = String(data.get('phone') || '').trim();
    if (!email) {
      this.showStatus('Enter an email address so FeelOri can verify this reservation.', 'error');
      return;
    }
    if (data.get('whatsapp_consent') === 'on' && !phone) {
      this.showStatus('Enter a phone number to receive optional WhatsApp updates.', 'error');
      return;
    }
    if (this.captchaSiteKey && !this.captchaToken) {
      this.showStatus('Complete the anti-bot check before reserving.', 'error');
      return;
    }

    this.setBusy(true);
    this.showStatus('Sending your free reservation…', '');

    const payload = {
      campaign_id: this.campaignId,
      name: String(data.get('name') || '').trim(),
      email,
      phone,
      honeypot: String(data.get('company') || '').trim(),
      device_token: this.deviceToken,
      captcha_token: this.captchaToken || (window.location.hostname === '127.0.0.1' || window.location.hostname === 'localhost' ? 'test' : ''),
      consent_version: 'crowddrop-pilot-v1',
      referral_code: String(data.get('referral_code') || '').trim(),
      consent: {
        campaign_contact: data.get('campaign_contact') === 'on',
        whatsapp: data.get('whatsapp_consent') === 'on',
        marketing: data.get('marketing_consent') === 'on',
      },
      source: {
        landing_url: `${window.location.origin}${window.location.pathname}`.slice(0, 1000),
        utm_source: new URLSearchParams(window.location.search).get('utm_source') || '',
        utm_medium: new URLSearchParams(window.location.search).get('utm_medium') || '',
        utm_campaign: new URLSearchParams(window.location.search).get('utm_campaign') || '',
      },
    };

    try {
      const response = await fetch(this.endpointUrl(), {
        method: 'POST',
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
        body: new URLSearchParams({ payload: JSON.stringify(payload) }),
      });
      const result = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(result.message || result.error || 'We could not save this reservation. Please try again.');
      }

      const reservation = result.reservation || result;
      const verified = reservation.status === 'verified';
      this.showStatus(
        verified
          ? 'Your reservation is verified and included in the shared price.'
          : result.message || 'Check your inbox. The shared price changes only after you verify.',
        'success'
      );
      if (verified) {
        this.form.reset();
        this.showSharePanel(true);
      } else {
        this.pendingReservationId = reservation.id;
        this.pendingEmail = email;
        const panel = this.querySelector('[data-verification-panel]');
        if (panel) panel.hidden = false;
        const verificationEmail = this.querySelector('[data-verification-email]');
        if (verificationEmail) verificationEmail.textContent = email;
      }
      this.applyCampaign(result.campaign);
    } catch (error) {
      this.showStatus(error.message || 'We could not save this reservation. Please try again.', 'error');
    } finally {
      this.setBusy(false);
    }
  }

  async verifyReservation(event) {
    event.preventDefault();
    const code = String(new FormData(event.currentTarget).get('verification_code') || '').trim();
    if (!this.pendingReservationId || !/^\d{6}$/.test(code)) {
      this.showStatus('Enter the six-digit code from your email.', 'error');
      return;
    }
    this.showStatus('Verifying your reservation…', '');
    try {
      const response = await fetch(this.endpointUrl(), {
        method: 'POST', credentials: 'same-origin', headers: { Accept: 'application/json' },
        body: new URLSearchParams({ payload: JSON.stringify({ action: 'verify', campaign_id: this.campaignId, reservation_id: this.pendingReservationId, email: this.pendingEmail, code }) }),
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(result.message || result.error || 'Verification failed.');
      this.showStatus(result.message || 'Your reservation is verified and included in the shared price.', 'success');
      this.querySelector('[data-verification-panel]')?.setAttribute('hidden', '');
      this.form?.reset();
      this.showSharePanel(true);
      this.applyCampaign(result.campaign);
    } catch (error) {
      this.showStatus(error.message || 'Verification failed.', 'error');
    }
  }

  async resendVerification() {
    if (!this.pendingEmail) return;
    try {
      const response = await fetch(this.endpointUrl(), {
        method: 'POST', credentials: 'same-origin', headers: { Accept: 'application/json' },
        body: new URLSearchParams({ payload: JSON.stringify({ action: 'resend', campaign_id: this.campaignId, email: this.pendingEmail }) }),
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(result.message || result.error || 'Resend failed.');
      this.showStatus(result.message || 'A new verification email was queued.', 'success');
    } catch (error) {
      this.showStatus(error.message || 'Resend failed.', 'error');
    }
  }

  showSharePanel(openPanel = false) {
    const shareUrl = this.campaignShareUrl();

    const panel = this.querySelector('[data-share-panel]');
    const input = this.querySelector('[data-referral-link]');
    const whatsapp = this.querySelector('[data-whatsapp-share]');
    if (input) input.value = shareUrl;
    if (whatsapp) whatsapp.href = `https://wa.me/?text=${encodeURIComponent(`${this.shareMessage} ${shareUrl}`)}`;
    if (panel) {
      panel.hidden = false;
      if (openPanel && panel.tagName === 'DETAILS') panel.open = true;
    }
  }

  campaignShareUrl() {
    const url = new URL(window.location.href);
    url.search = '';
    if (this.campaignId && this.campaignId !== this.initialCampaignId) url.searchParams.set('drop', this.campaignId);
    url.hash = '';
    return url.toString();
  }

  async copyReferral() {
    const input = this.querySelector('[data-referral-link]');
    if (!input?.value) return;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(input.value);
      } else {
        input.focus();
        input.select();
        if (!document.execCommand('copy')) throw new Error('Copy is not supported');
      }
      this.showStatus('Campaign link copied. Add it to an Instagram Link sticker or paste it into WhatsApp.', 'success');
    } catch (_error) {
      input.focus();
      input.select();
      this.showStatus('Select and copy the highlighted referral link.', 'error');
    }
  }

  async shareReferral() {
    const input = this.querySelector('[data-referral-link]');
    if (!input?.value) return;
    if (navigator.share) {
      try {
        await navigator.share({ title: 'FeelOri CrowdDrop', text: this.shareMessage, url: input.value });
        this.showStatus('Share options opened. Sharing is optional and never changes the price by itself.', 'success');
      } catch (error) {
        if (error?.name !== 'AbortError') {
          await this.copyReferral();
          this.showStatus('Device sharing is unavailable here, so the campaign link was copied instead.', 'success');
        }
      }
      return;
    }
    await this.copyReferral();
  }

  async shareInstagramStory() {
    const input = this.querySelector('[data-referral-link]');
    if (!input?.value) return;

    try {
      const blob = await this.createStoryCard(input.value);
      const file = new File([blob], 'feelori-crowddrop-story.png', { type: 'image/png' });
      const shareData = {
        files: [file],
        text: `${this.shareMessage} ${input.value}`,
        title: 'FeelOri CrowdDrop',
      };

      if (navigator.share && navigator.canShare?.({ files: [file] })) {
        try {
          await navigator.share(shareData);
          this.showStatus('Story card shared. Add the campaign URL with Instagram’s Link sticker if needed.', 'success');
        } catch (error) {
          if (error?.name !== 'AbortError') {
            this.downloadStoryCard(blob);
            await this.copyReferral();
            this.showStatus('Story card downloaded and campaign link copied. Add the card in Instagram, then use the Link sticker.', 'success');
          }
        }
      } else {
        this.downloadStoryCard(blob);
        await this.copyReferral();
        this.showStatus('Story card downloaded and campaign link copied. Add the card in Instagram, then use the Link sticker.', 'success');
      }
    } catch (_error) {
      this.showStatus('The Story card could not be created in this browser. Copy the campaign link and share it manually.', 'error');
    }
  }

  createStoryCard(referralUrl) {
    const canvas = document.createElement('canvas');
    canvas.width = 1080;
    canvas.height = 1920;
    const context = canvas.getContext('2d');
    if (!context) return Promise.reject(new Error('Canvas is unavailable'));

    context.fillStyle = '#fffaf2';
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.fillStyle = '#f1dfc8';
    context.beginPath();
    context.arc(930, 180, 330, 0, Math.PI * 2);
    context.fill();
    context.fillStyle = '#7c1832';
    context.beginPath();
    context.arc(80, 1760, 250, 0, Math.PI * 2);
    context.fill();

    context.fillStyle = '#7c1832';
    context.font = '700 34px Arial, sans-serif';
    context.letterSpacing = '5px';
    context.fillText('FEELORI CROWDDROP', 90, 190);

    context.fillStyle = '#551022';
    context.font = '700 92px Georgia, serif';
    this.drawWrappedText(context, this.dataset.storyTitle || 'Unlock one shared price, together.', 90, 380, 900, 112, 5);

    context.fillStyle = '#735f61';
    context.font = '42px Arial, sans-serif';
    this.drawWrappedText(context, 'The first three unique email-verified reservations each lower the shared price by ₹300.', 90, 780, 860, 60, 4);

    const currentPrice = this.querySelector('[data-current-price]')?.textContent?.trim() || '';
    context.fillStyle = '#7c1832';
    context.font = '700 36px Arial, sans-serif';
    context.fillText('CURRENT SHARED PRICE', 90, 1100);
    context.fillStyle = '#551022';
    context.font = '700 118px Georgia, serif';
    context.fillText(currentPrice, 90, 1235);

    context.fillStyle = '#735f61';
    context.font = '36px Arial, sans-serif';
    this.drawWrappedText(context, 'Reserve free. No payment now. Sharing is optional.', 90, 1395, 850, 54, 3);

    const readableUrl = referralUrl.length > 88 ? `${referralUrl.slice(0, 85)}…` : referralUrl;
    context.fillStyle = '#551022';
    context.font = '700 31px Arial, sans-serif';
    this.drawWrappedText(context, readableUrl, 90, 1585, 830, 46, 3);
    context.fillStyle = '#fffaf2';
    context.font = '700 30px Arial, sans-serif';
    context.fillText('Add the campaign URL with Instagram’s Link sticker', 90, 1800);

    return new Promise((resolve, reject) => {
      canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('Card export failed'))), 'image/png');
    });
  }

  drawWrappedText(context, text, x, y, maxWidth, lineHeight, maxLines) {
    const words = text.split(/\s+/);
    const lines = [];
    let line = '';
    words.forEach((word) => {
      const candidate = line ? `${line} ${word}` : word;
      if (context.measureText(candidate).width > maxWidth && line) {
        lines.push(line);
        line = word;
      } else {
        line = candidate;
      }
    });
    if (line) lines.push(line);
    lines.slice(0, maxLines).forEach((value, index) => context.fillText(value, x, y + index * lineHeight));
  }

  downloadStoryCard(blob) {
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'feelori-crowddrop-story.png';
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.setTimeout(() => URL.revokeObjectURL(link.href), 1000);
  }

  renderActivity(activity) {
    if (!Array.isArray(activity) || activity.length === 0) return;
    const section = this.querySelector('[data-activity]');
    const list = this.querySelector('[data-activity-list]');
    if (!section || !list) return;
    list.replaceChildren();
    activity.slice(0, 6).forEach((item) => {
      if (!item || typeof item.message !== 'string') return;
      const row = document.createElement('li');
      row.textContent = item.message;
      list.appendChild(row);
    });
    section.hidden = list.children.length === 0;
  }

  disableForm(message) {
    this.formDisabled = true;
    if (this.submitButton) this.submitButton.disabled = true;
    const notice = this.querySelector('[data-form-notice]');
    if (notice) {
      notice.textContent = message;
      notice.hidden = false;
    }
  }

  setBusy(busy) {
    if (!this.submitButton) return;
    this.submitButton.disabled = busy || this.formDisabled;
    this.submitButton.setAttribute('aria-busy', String(busy));
    this.submitButton.textContent = busy ? 'Reserving…' : this.submitButton.dataset.label;
  }

  showStatus(message, state) {
    if (!this.status) return;
    this.status.textContent = message;
    this.status.dataset.state = state;
  }

  money(minorUnits) {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: this.currency,
      maximumFractionDigits: minorUnits % 100 === 0 ? 0 : 2,
    }).format(minorUnits / 100);
  }

  formatDeadline(timestamp) {
    return new Intl.DateTimeFormat('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      timeZone: 'Asia/Kolkata',
      timeZoneName: 'short',
    }).format(timestamp);
  }

  asNumber(value) {
    const number = Number(value);
    return Number.isFinite(number) ? number : null;
  }

  setText(selector, value) {
    this.querySelectorAll(selector).forEach((element) => {
      element.textContent = value;
    });
  }
}

if (!customElements.get('feelori-crowddrop')) {
  customElements.define('feelori-crowddrop', FeelOriCrowdDrop);
}
